import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Drawer,
    Form,
    Input,
    Layout,
    Segmented,
    Space,
    Tag,
    Typography,
} from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
    DocumentAuditClaimedVaccinationDto,
    DocumentAuditControllerListRequest,
    DocumentAuditDecisionRequestDtoDecisionEnum,
    DocumentAuditDetailDto,
    DocumentAuditListItemDto,
    DocumentAuditListItemDtoStatusEnum,
    DocumentAuditUploadedByDto,
    DocumentAuditValidationResultDto,
} from '@clh/api-client';
import { unwrapError, useApiClient } from '@clh/ui';

import { useToast } from '../hooks/use-toast';
import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';
import { FetchRecordTable } from '../table';

dayjs.extend(relativeTime);

const formatName = (
    profile: DocumentAuditUploadedByDto | null
): string | null => {
    if (!profile) return null;
    const joined = [profile.firstName, profile.lastName]
        .filter(Boolean)
        .join(' ');
    return joined || null;
};

const STATUS_META: Record<
    DocumentAuditListItemDtoStatusEnum,
    { color: string; label: string }
> = {
    [DocumentAuditListItemDtoStatusEnum.NeedsReview]: {
        color: 'gold',
        label: 'Needs review',
    },
    [DocumentAuditListItemDtoStatusEnum.Approved]: {
        color: 'green',
        label: 'Approved',
    },
    [DocumentAuditListItemDtoStatusEnum.Rejected]: {
        color: 'red',
        label: 'Rejected',
    },
};

const STATUS_OPTIONS = Object.entries(STATUS_META).map(
    ([value, { label }]) => ({
        value: value as DocumentAuditListItemDtoStatusEnum,
        label,
    })
);

const DEFAULT_STATUS = DocumentAuditListItemDtoStatusEnum.NeedsReview;

export const DocumentAudits: FC = () => {
    const api = useApiClient();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [refetchToken, setRefetchToken] = useState(0);

    if (!api) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Document Audits</h2>
            </Header>
            <Content>
                <FetchRecordTable
                    key={refetchToken}
                    rowKey="storedDocumentId"
                    paginated
                    cacheKey="documentAuditControllerList"
                    getRecords={(
                        params?: Partial<DocumentAuditControllerListRequest>
                    ) =>
                        api.documentAuditControllerList({
                            ...params,
                            status: params?.status ?? DEFAULT_STATUS,
                        })
                    }
                    filters={{
                        filters: {
                            status: {
                                label: 'Status',
                                noToggleButton: true,
                                control: ({ onApply, params }) => (
                                    <Segmented
                                        value={params?.status ?? DEFAULT_STATUS}
                                        options={STATUS_OPTIONS}
                                        onChange={(value) => onApply(value)}
                                    />
                                ),
                            },
                        },
                    }}
                    onRow={(record: DocumentAuditListItemDto) => ({
                        onClick: () => setSelectedId(record.storedDocumentId),
                        style: { cursor: 'pointer' },
                    })}
                    columns={[
                        {
                            title: 'Nurse',
                            dataIndex: 'uploadedBy',
                            key: 'nurse',
                            render: (
                                uploadedBy: DocumentAuditUploadedByDto | null
                            ) =>
                                formatName(uploadedBy) ?? (
                                    <Typography.Text type="secondary">
                                        Unknown
                                    </Typography.Text>
                                ),
                        },
                        {
                            title: 'Document type',
                            dataIndex: 'documentType',
                            key: 'documentType',
                            render: (value: string) => value.replace(/_/g, ' '),
                        },
                        {
                            title: 'File',
                            dataIndex: 'fileName',
                            key: 'fileName',
                        },
                        {
                            title: 'Uploaded',
                            dataIndex: 'uploadedAt',
                            key: 'uploadedAt',
                            render: (value: Date) => (
                                <span
                                    title={dayjs(value).format(
                                        'YYYY-MM-DD HH:mm'
                                    )}
                                >
                                    {dayjs(value).fromNow()}
                                </span>
                            ),
                        },
                        {
                            title: 'Status',
                            dataIndex: 'status',
                            key: 'status',
                            render: (
                                value: DocumentAuditListItemDtoStatusEnum,
                                record: DocumentAuditListItemDto
                            ) => {
                                const tag = STATUS_META[value];
                                const result = record.humanResult;
                                const reviewerName = formatName(
                                    result?.reviewedBy ?? null
                                );
                                return (
                                    <Space direction="vertical" size={0}>
                                        <Tag color={tag.color}>{tag.label}</Tag>
                                        {result && (
                                            <Typography.Text
                                                type="secondary"
                                                style={{ fontSize: 12 }}
                                            >
                                                {dayjs(
                                                    result.createdAt
                                                ).fromNow()}
                                                {reviewerName
                                                    ? ` by ${reviewerName}`
                                                    : ''}
                                            </Typography.Text>
                                        )}
                                    </Space>
                                );
                            },
                        },
                    ]}
                />
                <ReviewDrawer
                    storedDocumentId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onDecisionRecorded={() => {
                        setSelectedId(null);
                        setRefetchToken((t) => t + 1);
                    }}
                />
            </Content>
        </Layout>
    );
};

type ReviewDrawerProps = {
    storedDocumentId: string | null;
    onClose: () => void;
    onDecisionRecorded: () => void;
};

const ReviewDrawer: FC<ReviewDrawerProps> = ({
    storedDocumentId,
    onClose,
    onDecisionRecorded,
}) => {
    const api = useApiClient();
    const { notification } = useToast();
    const [detail, setDetail] = useState<DocumentAuditDetailDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm<{ notes?: string }>();

    useEffect(() => {
        if (!api || !storedDocumentId) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        form.resetFields();
        api.documentAuditControllerGetDetail({ storedDocumentId })
            .then((value) => {
                if (!cancelled) setDetail(value);
            })
            .catch((err) => {
                // biome-ignore lint/suspicious/noConsole: error logging
                console.error(
                    '[DocumentAudits] Failed to load document',
                    storedDocumentId,
                    err
                );
                if (!cancelled) {
                    notification.error({
                        message: 'Failed to load document',
                        description: unwrapError(err),
                        duration: 5,
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [api, storedDocumentId, form, notification]);

    const submitDecision = async (
        decision: DocumentAuditDecisionRequestDtoDecisionEnum
    ) => {
        if (!api || !storedDocumentId) return;
        const values = await form.validateFields();
        const isReject =
            decision === DocumentAuditDecisionRequestDtoDecisionEnum.Rejected;

        try {
            setSubmitting(true);
            await api.documentAuditControllerRecordDecision({
                storedDocumentId,
                documentAuditDecisionRequestDto: {
                    decision,
                    notes: values.notes,
                },
            });
            notification.success({
                message: isReject ? 'Document rejected' : 'Document approved',
                duration: 3,
            });
            onDecisionRecorded();
        } catch (err) {
            // biome-ignore lint/suspicious/noConsole: error logging
            console.error(
                '[DocumentAudits] Failed to record decision',
                { storedDocumentId, decision },
                err
            );
            notification.error({
                message: 'Failed to record decision',
                description: unwrapError(err),
                duration: 5,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={
                detail ? (
                    <Space size="small" wrap>
                        <Typography.Text strong>
                            {detail.uploadedBy?.profileId ? (
                                <Link
                                    to={`/profiles/${detail.uploadedBy.profileId}`}
                                >
                                    {formatName(detail.uploadedBy) ??
                                        'Unknown nurse'}
                                </Link>
                            ) : (
                                'Unknown nurse'
                            )}
                        </Typography.Text>
                        <Typography.Text type="secondary">
                            · {detail.documentType.replace(/_/g, ' ')} ·
                            uploaded{' '}
                            {dayjs(detail.uploadedAt).format(
                                'YYYY-MM-DD HH:mm'
                            )}
                        </Typography.Text>
                    </Space>
                ) : (
                    'Review document'
                )
            }
            placement="right"
            width="80%"
            open={!!storedDocumentId}
            onClose={onClose}
            destroyOnClose
            extra={
                detail && (
                    <Space>
                        <Button
                            danger
                            icon={<CloseOutlined />}
                            loading={submitting}
                            onClick={() =>
                                void submitDecision(
                                    DocumentAuditDecisionRequestDtoDecisionEnum.Rejected
                                )
                            }
                        >
                            Reject
                        </Button>
                        <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={submitting}
                            onClick={() =>
                                void submitDecision(
                                    DocumentAuditDecisionRequestDtoDecisionEnum.Approved
                                )
                            }
                        >
                            Approve
                        </Button>
                    </Space>
                )
            }
        >
            {loading || !detail ? (
                <Spinner />
            ) : (
                <Space
                    direction="vertical"
                    style={{ width: '100%' }}
                    size="middle"
                >
                    <DocumentPreview detail={detail} />
                    <ClaimedVaccinations
                        vaccinations={detail.claimedVaccinations}
                    />
                    {detail.humanResult && (
                        <Alert
                            type={
                                detail.humanResult.decision ===
                                DocumentAuditDecisionRequestDtoDecisionEnum.Approved
                                    ? 'success'
                                    : 'warning'
                            }
                            showIcon
                            message={
                                <Space size="small" wrap>
                                    <Typography.Text strong>
                                        Prior decision:
                                    </Typography.Text>
                                    <Tag>{detail.humanResult.decision}</Tag>
                                    <Typography.Text type="secondary">
                                        {dayjs(
                                            detail.humanResult.createdAt
                                        ).fromNow()}
                                        {formatName(
                                            detail.humanResult.reviewedBy
                                        )
                                            ? ` by ${formatName(detail.humanResult.reviewedBy)}`
                                            : ''}
                                    </Typography.Text>
                                </Space>
                            }
                            description={detail.humanResult.notes ?? undefined}
                        />
                    )}
                    {detail.llmResult && (
                        <Alert
                            type="info"
                            message="LLM pre-screen (advisory)"
                            description={
                                <LlmResultSummary result={detail.llmResult} />
                            }
                            showIcon
                        />
                    )}
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="notes"
                            label="Reviewer notes (optional)"
                            rules={[{ max: 2000 }]}
                        >
                            <Input.TextArea
                                rows={2}
                                placeholder="Why are you approving or rejecting?"
                            />
                        </Form.Item>
                    </Form>
                </Space>
            )}
        </Drawer>
    );
};

const DocumentPreview: FC<{ detail: DocumentAuditDetailDto }> = ({
    detail,
}) => {
    const isImage = detail.mimeType.startsWith('image/');
    const isPdf = detail.mimeType === 'application/pdf';

    const openInNewTab = (
        <Typography.Link
            href={detail.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
        >
            Open {detail.fileName} in new tab
        </Typography.Link>
    );

    if (isImage) {
        return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <img
                    src={detail.downloadUrl}
                    alt={detail.fileName}
                    style={{
                        maxWidth: '100%',
                        maxHeight: '70vh',
                        objectFit: 'contain',
                        border: '1px solid #f0f0f0',
                    }}
                />
                {openInNewTab}
            </Space>
        );
    }

    if (isPdf) {
        return (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <iframe
                    title={detail.fileName}
                    src={detail.downloadUrl}
                    style={{
                        width: '100%',
                        height: '70vh',
                        border: '1px solid #f0f0f0',
                    }}
                />
                {openInNewTab}
            </Space>
        );
    }

    return (
        <Alert
            type="warning"
            showIcon
            message={`Inline preview unavailable for ${detail.mimeType}`}
            description={openInNewTab}
        />
    );
};

const LlmResultSummary: FC<{ result: DocumentAuditValidationResultDto }> = ({
    result,
}) => (
    <Space direction="vertical" size={0}>
        <Typography.Text>
            Decision: <Tag>{result.decision}</Tag>
        </Typography.Text>
        <Typography.Text type="secondary">
            Recorded {dayjs(result.createdAt).fromNow()}
        </Typography.Text>
    </Space>
);

const ClaimedVaccinations: FC<{
    vaccinations: DocumentAuditClaimedVaccinationDto[];
}> = ({ vaccinations }) => (
    <Card size="small" title="What the nurse entered">
        {vaccinations.length === 0 ? (
            <Typography.Text type="secondary">
                No vaccinations are linked to this document.
            </Typography.Text>
        ) : (
            vaccinations.map((v) => (
                <div key={v.code}>
                    <Typography.Text strong>
                        {v.code.replace(/_/g, ' ')}
                        {v.hasTiter ? ' (titer)' : ''}
                        {v.expiresAt
                            ? ` · expires ${dayjs(v.expiresAt).format('YYYY-MM-DD')}`
                            : ''}
                    </Typography.Text>
                    {v.doses.length === 0 ? (
                        <div>
                            <Typography.Text type="secondary">
                                No dose dates entered
                            </Typography.Text>
                        </div>
                    ) : (
                        v.doses.map((d, i) => (
                            <div key={`${d.date}-${d.manufacturer}-${i}`}>
                                <Typography.Text type="secondary">
                                    {d.date}
                                    {d.manufacturer
                                        ? ` · ${d.manufacturer.replace(/_/g, ' ')}`
                                        : ''}
                                </Typography.Text>
                            </div>
                        ))
                    )}
                </div>
            ))
        )}
    </Card>
);
